import React, { useState } from 'react';
import { Palette, Check, Sliders, Layout, Info, Heart, Award, Sparkles, RefreshCw } from 'lucide-react';
import { useLanguage } from '../lib/i18n';

interface ThemeModuleProps {
  themeAccent?: string;
  onThemeAccentChange?: (accent: string) => void;
}

export default function BrandingModule({ themeAccent = 'indigo', onThemeAccentChange }: ThemeModuleProps) {
  const { lang, t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const presets = [
    { 
      id: 'indigo', 
      name: t('brandingPresetIndigoName', 'آبی آرامش (پیش‌فرض)'), 
      colorClass: 'bg-indigo-500', 
      desc: t('brandingPresetIndigoDesc', 'آرام، متعادل و عالی برای تمرکز و پایداری بر عادت‌ها.'),
      accentClass: 'text-indigo-500 border-indigo-200 bg-indigo-50/30'
    },
    { 
      id: 'emerald', 
      name: t('brandingPresetEmeraldName', 'سبز تندرستی و بهبود'), 
      colorClass: 'bg-emerald-500', 
      desc: t('brandingPresetEmeraldDesc', 'طبیعی، تداعی‌کننده احیا، سلامتی و توانبخشی فعال.'),
      accentClass: 'text-emerald-500 border-emerald-200 bg-emerald-50/30'
    },
    { 
      id: 'amber', 
      name: t('brandingPresetAmberName', 'نارنجی طلایی و انرژی'), 
      colorClass: 'bg-amber-500', 
      desc: t('brandingPresetAmberDesc', 'پویا، گرم و ایجاد انگیزه فراوان برای تمرینات کاردیو.'),
      accentClass: 'text-amber-500 border-amber-200 bg-amber-50/30'
    },
    { 
      id: 'rose', 
      name: t('brandingPresetRoseName', 'صورتی مدرن و شادابی'), 
      colorClass: 'bg-rose-500', 
      desc: t('brandingPresetRoseDesc', 'مدرن، با نشاط و الهام‌بخش روتین‌های روزانه خودمراقبتی.'),
      accentClass: 'text-rose-500 border-rose-200 bg-rose-50/30'
    },
    { 
      id: 'violet', 
      name: t('brandingPresetVioletName', 'بنفش عمیق و خلاقیت'), 
      colorClass: 'bg-violet-500', 
      desc: t('brandingPresetVioletDesc', 'عمیق، الهام‌بخش برای مطالعه مستمر زبان سوئدی و تمرکز.'),
      accentClass: 'text-violet-500 border-violet-200 bg-violet-50/30'
    },
    { 
      id: 'sky', 
      name: t('brandingPresetSkyName', 'آسمان روشن و زلال'), 
      colorClass: 'bg-sky-500', 
      desc: t('brandingPresetSkyDesc', 'سبک، آرامش‌بخش و نماد ذهنی باز و بدون دغدغه.'),
      accentClass: 'text-sky-500 border-sky-200 bg-sky-50/30'
    }
  ];

  const handleSelectAccent = (id: string) => {
    if (onThemeAccentChange) {
      onThemeAccentChange(id);
    }
  };

  return (
    <div id="theme-customizer-view" className="space-y-6 animate-fadeIn">
      
      {/* Module Title Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <Palette className="w-6 h-6 text-indigo-500" />
            {t('brandingHeaderTitle', 'تنظیم پوسته و رنگ‌های برنامه پیشرفت')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('brandingHeaderSub', 'رنگ شاخص و فضای بصری میز کار خود را متناسب با سلیقه و انگیزه روزانه خود شخصی‌سازی کنید.')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Preset Selector List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="premium-card p-6 dark:bg-gray-800 space-y-4">
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Sliders className="w-5 h-5 text-indigo-500" />
              {t('brandingAccentTitle', 'انتخاب پالت رنگ اصلی (Accent Color)')}
            </h3>
            
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('brandingAccentSub', 'با انتخاب هر کدام از پالت‌های زیر، تمام دکمه‌ها، نمودارها، نوارهای پیشرفت و آیکون‌های برنامه به صورت زنده به آن رنگ آراسته خواهند شد.')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-2">
              {presets.map((preset) => {
                const isSelected = themeAccent === preset.id;
                return (
                  <button
                    key={preset.id}
                    onClick={() => handleSelectAccent(preset.id)}
                    className={`p-4 rounded-2xl border transition-all flex items-start gap-3 hover:scale-[1.01] active:scale-[0.99] cursor-pointer ${
                      isSelected 
                        ? 'border-indigo-500 bg-indigo-50/30 dark:bg-indigo-950/20 ring-1 ring-indigo-500' 
                        : 'border-gray-100 dark:border-gray-700/60 bg-gray-50/20 dark:bg-gray-900/10 hover:border-gray-200 dark:hover:border-gray-700'
                    }`}
                  >
                    {/* Circle Color Indicator */}
                    <div className={`w-5.5 h-5.5 rounded-full ${preset.colorClass} shrink-0 mt-0.5 flex items-center justify-center text-white`}>
                      {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </div>
                    
                    <div className="space-y-1">
                      <span className="font-bold text-xs text-gray-900 dark:text-white block">
                        {preset.name}
                      </span>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                        {preset.desc}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Core App Rebranding Note */}
          <div className="premium-card p-6 dark:bg-gray-800 space-y-3.5">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Info className="w-4.5 h-4.5 text-indigo-500" />
              {t('brandingAboutTitle', 'درباره نام تجاری جدید «پیشرفت»')}
            </h3>
            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-2 leading-relaxed">
              <p>
                {t('brandingAboutP1', 'نام پیشین برنامه (Växa / وکسا) کلمه‌ای سوئدی به معنی رشد تدریجی بود. بنابر بازخوردهای ارزشمند کاربران و در جهت بومی‌سازی ملموس‌تر، نام رسمی برنامه به «پیشرفت» تغییر یافت.')}
              </p>
              <p>
                {t('brandingAboutP2', 'برنامه پیشرفت یک همیار خودمراقبتی و هوشمند با ذخیره‌سازی ابری ایمن است که مأموریت آن بهبود دیسک کمر، کاهش چربی شکم، پایش تغذیه آسان، یادگیری روزانه سوئدی و ارائه گزارش‌های بهینه شده با هوش مصنوعی جمینی در یک محیط کاملا خصوصی و همگام است.')}
              </p>
            </div>
          </div>
        </div>

        {/* Live Interactive Preview Screen */}
        <div className="space-y-4">
          <div className="premium-card p-6 dark:bg-gray-800 space-y-5">
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Layout className="w-5 h-5 text-indigo-500" />
              {t('brandingPreviewTitle', 'پیش‌نمایش زنده پوسته')}
            </h3>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('brandingPreviewSub', 'تغییرات رابط کاربری را به صورت آنی در این ماکاپ زنده مشاهده کنید:')}
            </p>

            {/* Simulated UI Mockup Card */}
            <div className="p-4.5 bg-gray-50 dark:bg-gray-950 rounded-2xl border border-gray-100 dark:border-gray-900 space-y-4 select-none">
              
              {/* Fake Header */}
              <div className="flex items-center justify-between border-b pb-2 border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 block"></span>
                  <span className="font-bold text-[10px] text-gray-800 dark:text-gray-200">{t('brandingMockHeader', 'پیش‌نمایش داشبورد')}</span>
                </div>
                <span className="text-[8px] bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 font-bold px-1.5 py-0.5 rounded-full font-mono">
                  {t('brandingMockCompleted', '۷۲٪ کامل شده')}
                </span>
              </div>

              {/* Fake Progress Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-[8px] font-bold text-gray-400">
                  <span>{t('brandingMockHabitProgress', 'میزان پیشرفت روزانه عادت‌ها')}</span>
                  <span>۷۲٪</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-800 h-2 rounded-full overflow-hidden">
                  <div className="bg-indigo-500 h-full rounded-full transition-all duration-300" style={{ width: '72%' }}></div>
                </div>
              </div>

              {/* Fake Buttons & Badges */}
              <div className="grid grid-cols-2 gap-2 text-[9px]">
                <button className="py-1.5 px-2 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-lg text-center transition-colors">
                  {t('brandingMockSaveBtn', 'ذخیره تغییرات')}
                </button>
                <div className="py-1.5 px-2 border border-indigo-200 text-indigo-600 dark:border-indigo-900/50 dark:text-indigo-400 bg-indigo-50/20 rounded-lg font-bold text-center">
                  {t('brandingMockRehabBtn', 'انجام فیزیوتراپی')}
                </div>
              </div>

              {/* Fake Habit Status */}
              <div className="p-2.5 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800/80 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-indigo-500 stroke-[3.5]" />
                  <span className="text-[9px] font-semibold text-gray-700 dark:text-gray-300">{t('brandingMockBackExercise', 'تمرین کمر (سگ پرنده)')}</span>
                </div>
                <span className="text-[8px] text-gray-400 font-mono">{t('brandingMockTwoSets', '۲ ست کامل')}</span>
              </div>

            </div>

            <div className="pt-2 text-center">
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-1 bg-emerald-500/10 py-2 rounded-xl border border-emerald-500/20">
                <Sparkles className="w-4 h-4 animate-pulse" />
                {t('brandingSuccessMessage', 'تغییرات با موفقیت بر کل سیستم اعمال شد')}
              </span>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}
